import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import image3 from '../assets/1.webp';
import ScrollToBottomButton from '../components/ui/ScrollToBottomButton';

const Slider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isInteracting, setIsInteracting] = useState(false);
  const sliderRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  }, [images.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    setIsInteracting(true);
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      goToNext();
    }

    if (touchStartX.current - touchEndX.current < -50) {
      goToPrevious();
    }
    setIsInteracting(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop && !isInteracting) {
      const interval = setInterval(() => {
        goToNext();
      }, 4000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [isDesktop, isInteracting, goToNext]);

  return (
    <div
      className="relative w-full h-48 overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      ref={sliderRef}
    >
      {images.map((image, index) => (
        <img
          key={index}
          src={image}
          alt={`Slide ${index}`}
          className={`absolute w-full h-full object-cover transition-opacity duration-500 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {isDesktop && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white bg-opacity-50 text-white p-2 rounded-full z-50 hover:bg-opacity-75 transition-opacity duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="#0F3D2E">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white bg-opacity-50 text-white p-2 rounded-full z-50 hover:bg-opacity-75 transition-opacity duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="#0F3D2E">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

const attractions = [
  {
    name: 'Darjeeling Himalayan Railway',
    description: 'A UNESCO World Heritage site, offering a nostalgic journey through breathtaking landscapes and terraced tea plantations.',
    image: '/a.webp',
  },
  {
    name: 'Tea Estates',
    description: 'Visit Makaibari and Happy Valley Tea Estates to witness the intricate process of tea production and savor world-renowned brews.',
    image: '/b.webp',
  },
  {
    name: 'Tiger Hill',
    description: 'A pre-dawn pilgrimage for awe-inspiring sunrises that bathe the Kanchenjunga Mountain range in ethereal hues.',
    image: '/c.webp',
  },
  {
    name: 'Peace Pagoda & Batasia Loop',
    description: 'The Peace Pagoda is a symbol of harmony, while the Batasia Loop offers splendid views of Darjeeling from its beautiful garden.',
    images: ['/d.webp', '/z.webp'],
    isSlider: true,
  },
  {
    name: 'Padmaja Naidu Himalayan Zoological Park',
    description: 'A haven for wildlife enthusiasts, home to the rare red panda and the elusive snow leopard.',
    image: '/e.webp',
  },
  {
    name: 'Monasteries',
    description: 'Explore Ghoom Monastery and Yiga Choeling Monastery to immerse yourself in Buddhist traditions and artistry.',
    image: '/f.webp',
  },
];

const Attractions = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <>
      <section
        className="relative h-[60vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${image3})` }}
        data-aos="fade-in"
      >
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-aos="fade-up">Explore Darjeeling</h1>
          <p className="mt-4 text-lg text-gray-300" data-aos="fade-up" data-aos-delay="100">Discover the treasures of the Himalayas</p>
        </div>
        <ScrollToBottomButton targetId="attractions-content" />
      </section>

      <main id="attractions-content" className="bg-gray-50 py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl font-semibold text-dark-green">Nearby Attractions</h2>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              Darjeeling, a veritable treasure trove of natural beauty and cultural richness, promises an unforgettable experience for all who visit.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {attractions.map((attraction, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300" data-aos="fade-up">
                {attraction.isSlider ? (
                  <Slider images={attraction.images} />
                ) : (
                  <img src={attraction.image} alt={attraction.name} className="w-full h-48 object-cover" />
                )}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-dark-green mb-2">{attraction.name}</h3>
                  <p className="text-gray-700 leading-relaxed">{attraction.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </>
  );
};

export default Attractions;